// Shared data-loading hooks for the CRM panels of the lead detail (Tickets,
// Timeline, and the upcoming Calls / kanban panels). They centralise the three
// things every CRM panel repeated by hand:
//
//   1. A `cancelled` guard so a stale in-flight request (lead A) can never write
//      into the state after the deps changed (lead B).
//   2. Silencing of 401 (the CrmConnectGate handles reconnection; a 401 must not
//      paint a red fatal-error view).
//   3. A SYNCHRONOUS reset of the data when the deps change, so the panel never
//      shows lead A's content — or a fatal error latched from lead A — while
//      lead B is loading. This is the fix for "timeline of lead A visible in
//      lead B" and for the error view hiding the fresh load.
//
// Keep these dependency-light (only react + crm.ts) so any CRM panel can adopt
// them without pulling view-specific code.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CrmHttpError } from "../api/crm";

// Serialise the deps into a primitive key. The hooks reset synchronously on the
// first render where this key differs from the previous one (useState lazy init
// + a render-phase compare), which is what makes the reset happen BEFORE paint.
function depsKey(deps: ReadonlyArray<unknown>): string {
  return JSON.stringify(deps);
}

export type CrmResource<T> = {
  /** Loaded data, or null while first-loading / after a silenced 401. */
  data: T | null;
  loading: boolean;
  /** User-facing error message, or null. 401 is silenced (never set). */
  error: string | null;
  /**
   * HTTP status of the CrmHttpError that produced `error`, or null. Lets a view
   * distinguish e.g. a 404 (resource not found — offer "Volver") from a generic
   * failure (offer "Reintentar"). Null whenever `error` is null or the failure
   * was not a CrmHttpError.
   */
  errorStatus: number | null;
  /** Force a reload (Reintentar / after a mutation). */
  reload: () => void;
  /**
   * Local override of the data. Accepts a functional updater and is the single
   * source of truth for optimistic consumers (e.g. the tickets board moves a
   * card with `setData(prev => move(prev, ...))`).
   *
   * Contract for optimistic consumers:
   *  - ALWAYS use the functional form: it receives the freshest data, so an
   *    optimistic write composes over whatever a concurrent refetch just landed
   *    instead of clobbering it with a stale snapshot.
   *  - A deps change resets data to null synchronously (see below) and starts a
   *    fresh load; a pending optimistic write is intentionally discarded because
   *    it belonged to the previous deps. External setData never fights that
   *    reset — it only mutates the CURRENT deps' data.
   *  - `reload()` refetches WITHOUT wiping data first, so an in-flight optimistic
   *    write survives until the server response replaces it (reconciliation).
   */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
};

/**
 * Load a single CRM resource with items/loading/error/reload. `fetcher` gets an
 * AbortSignal-free cancellation via the returned guard; it must be stable enough
 * that `deps` capture everything it reads (lint enforces nothing here — the
 * caller passes deps explicitly, mirroring useEffect).
 */
export function useCrmResource<T>(
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  errorMessage: string,
): CrmResource<T> {
  const key = depsKey(deps);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Synchronous reset when deps change: wipe data/error and re-arm loading in
  // the render phase, before the effect runs, so no stale content is painted.
  const prevKey = useRef(key);
  if (prevKey.current !== key) {
    prevKey.current = key;
    setData(null);
    setError(null);
    setErrorStatus(null);
    setLoading(true);
  }

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // fetcher is intentionally not in the dep array; the caller-supplied `deps`
  // are the source of truth for when to refetch (same contract as useEffect).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    (async () => {
      try {
        const res = await fetcherRef.current();
        if (cancelled) return;
        setData(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof CrmHttpError && err.status === 401) {
          // The gate handles reconnection; do not paint a red error.
          setData(null);
        } else {
          setError(errorMessage);
          setErrorStatus(err instanceof CrmHttpError ? err.status : null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reloadKey, errorMessage]);

  return { data, loading, error, errorStatus, reload, setData };
}

export type CrmCursorPage<T> = { items: T[]; nextCursor: string | null };

export type CrmCursorList<T> = {
  items: T[] | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  nextCursor: string | null;
  reload: () => void;
  loadMore: () => void;
  /** Local override of items (e.g. optimistic prepend). */
  setItems: React.Dispatch<React.SetStateAction<T[] | null>>;
};

/**
 * Cursor-paginated CRM list: first page + accumulative loadMore. Both the first
 * page AND loadMore carry a cancellation/deps guard so a lead switch mid-page
 * can never mix items or cross cursors between leads. Items and cursor reset
 * synchronously on a deps change.
 */
export function useCrmCursorList<T>(
  fetchPage: (before?: string) => Promise<CrmCursorPage<T>>,
  deps: ReadonlyArray<unknown>,
  firstPageError: string,
  loadMoreError: string,
): CrmCursorList<T> {
  const key = depsKey(deps);
  const [items, setItems] = useState<T[] | null>(null);
  const [nextCursor, setNextCursorState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Guard shared by first-page effect and loadMore: bumped on every deps change
  // and on unmount, so any in-flight page that resolves late is discarded.
  const runId = useRef(0);
  // Cursor mirror so loadMore reads the latest value without a stale closure.
  const cursorRef = useRef<string | null>(null);
  const setNextCursor = useCallback((c: string | null) => {
    cursorRef.current = c;
    setNextCursorState(c);
  }, []);

  // Synchronous reset on deps change.
  const prevKey = useRef(key);
  if (prevKey.current !== key) {
    prevKey.current = key;
    runId.current += 1;
    cursorRef.current = null;
    setItems(null);
    setNextCursorState(null);
    setError(null);
    setLoading(true);
    setLoadingMore(false);
  }

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  useEffect(() => {
    runId.current += 1;
    const myRun = runId.current;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetchPageRef.current();
        if (runId.current !== myRun) return;
        setItems(res.items);
        cursorRef.current = res.nextCursor;
        setNextCursorState(res.nextCursor);
      } catch (err) {
        if (runId.current !== myRun) return;
        if (err instanceof CrmHttpError && err.status === 401) {
          setItems(null);
        } else {
          setError(firstPageError);
        }
      } finally {
        if (runId.current === myRun) setLoading(false);
      }
    })();
    return () => {
      // Invalidate this run so a late resolve is dropped.
      runId.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reloadKey, firstPageError]);

  const loadMore = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor) return;
    // Snapshot the current run: if the lead switches (deps change) while this
    // page is in flight, runId advances and we drop the result — no cross-lead
    // mixing, no cursor from lead A appended into lead B.
    const myRun = runId.current;
    let started = false;
    setLoadingMore((already) => {
      if (already) return already;
      started = true;
      return true;
    });
    if (!started) return;
    try {
      const res = await fetchPageRef.current(cursor);
      if (runId.current !== myRun) return;
      setItems((prev) => [...(prev ?? []), ...res.items]);
      setNextCursor(res.nextCursor);
    } catch {
      if (runId.current !== myRun) return;
      setError(loadMoreError);
    } finally {
      if (runId.current === myRun) setLoadingMore(false);
    }
  }, [loadMoreError, setNextCursor]);

  return {
    items,
    loading,
    loadingMore,
    error,
    nextCursor,
    reload,
    loadMore,
    setItems,
  };
}

// Shape a page-numbered CRM list must return so the hook can derive paging math.
export type CrmPagedResponse<T> = {
  items: T[];
  total: number;
  /** Server-authoritative page size — used (not a local constant) for the
   * range/hasNext math, so it stays correct if the server clamps `limit`. */
  pageSize: number;
};

export type CrmPagedList<T> = {
  /** The current page's rows (empty array while first-loading). */
  items: T[];
  total: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  /** 1-based index of the first row on this page (0 when total is 0). */
  pageStart: number;
  /** 1-based index of the last row on this page. */
  pageEnd: number;
  hasPrev: boolean;
  hasNext: boolean;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

/**
 * Page-numbered CRM list on top of useCrmResource. Owns `page` and resets it to
 * 1 whenever the filter deps change (so a narrower filter never lands on an
 * out-of-range page). `deps` are the FILTERS only — do not include page; the
 * hook injects the current page into the fetch itself. `pageStart/pageEnd/
 * hasNext` derive from the server-returned `pageSize`, never a local constant.
 */
export function useCrmPagedList<T>(
  fetchPage: (page: number) => Promise<CrmPagedResponse<T>>,
  deps: ReadonlyArray<unknown>,
  errorMessage: string,
): CrmPagedList<T> {
  const [page, setPage] = useState(1);

  // Reset to page 1 synchronously when the filters change, before the fetch
  // effect runs — mirrors the render-phase reset in useCrmResource.
  const filterKey = depsKey(deps);
  const prevFilterKey = useRef(filterKey);
  if (prevFilterKey.current !== filterKey) {
    prevFilterKey.current = filterKey;
    if (page !== 1) setPage(1);
  }

  const resource = useCrmResource<CrmPagedResponse<T>>(
    () => fetchPage(page),
    [filterKey, page],
    errorMessage,
  );

  const items = resource.data?.items ?? [];
  const total = resource.data?.total ?? 0;
  // Fall back to the current items length until the server tells us the real
  // page size, so the range never reads as 1..0 on the very first paint.
  const pageSize = resource.data?.pageSize ?? items.length ?? 0;

  return useMemo(() => {
    const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const pageEnd = Math.min(page * pageSize, total);
    return {
      items,
      total,
      page,
      setPage,
      pageStart,
      pageEnd,
      hasPrev: page > 1,
      hasNext: pageEnd < total,
      loading: resource.loading,
      error: resource.error,
      reload: resource.reload,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, total, page, pageSize, resource.loading, resource.error, resource.reload]);
}
